import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { BuyProvider } from '../MwPage/components/BuyButton'
import PressStrip from '../MwPage/components/PressStrip'
import StickyCta, { useStickyCta } from '../MwPage/components/StickyCta'
import { useOffer } from '../MwPage/offer'
import FooterSection from '../MwPage/sections/FooterSection'
import { PageRoot, Wrap as MwWrap } from '../MwPage/styled'
import IntakeModal from '../components/IntakeModal'
import VideoLightbox from '../components/VideoLightbox'
import { useJumpstartModal } from '../jumpstartModal'
import { fillTrial, trialModal } from './content/trial'
import { FocusProvider } from './focus'
import { ScoresProvider } from './scores'
import AdviceSection from './sections/AdviceSection'
import BlockerSection from './sections/BlockerSection'
import CompareSection from './sections/CompareSection'
import FaqSection from './sections/FaqSection'
import FinalSection from './sections/FinalSection'
import HeroSection from './sections/HeroSection'
import InviteSection from './sections/InviteSection'
import JourneySection from './sections/JourneySection'
import MemberVideosSection from './sections/MemberVideosSection'
import MentorSection from './sections/MentorSection'
import OfferSection from './sections/OfferSection'
import ProblemSection from './sections/ProblemSection'
import ProofSection from './sections/ProofSection'
import ScorerSection from './sections/ScorerSection'
import StageSection from './sections/StageSection'
import TestimonialsSection from './sections/TestimonialsSection'
import TransformationSection from './sections/TransformationSection'
import TrialScorerSection from './sections/TrialScorerSection'
import TrialTransformationSection from './sections/TrialTransformationSection'
import VideoSection from './sections/VideoSection'

// the MW page's footer, inside this page's column
const Narrow = styled.div`
  ${MwWrap} {
    max-width: 760px;
  }
`

/**
 * The Jumpstart page for one pain point, at /jumpstart-mw-ca (customer acquisition) and /jumpstart-mw-ri
 * (raising investment), built on 19 October 2026 from Martin's two HTML prototypes. The two share this layout,
 * the MW page's tokens and components, and differ only in `content` (see content/). The sliders in "Where are
 * you stuck?" write a targeted plan and place the visitor on the ladder further down (scorer.js, scores.js).
 *
 * Every buy button opens the intake modal, which is the purchase path the prototype's checkout link stands for.
 *
 * A trial page (content.trial, at /jumpstart-mw-ca-trial and /jumpstart-mw-ri-trial, from the 26 October 2026
 * prototypes) sells the 7-day free trial with the same layout minus "Sound familiar?" and the ladder, a simpler
 * scorer, lists in place of the drop-out chart, and the member videos under the press strip. The whole-business
 * trial page at /jumpstart-mw-trial (content/jumpstartTrial.js, 29 October 2026) is the same layout with no focus.
 *
 * The raising investment page (content/raisingInvestment.js, from the 2 November 2026 prototype, at /jumpstart-mw-ri,
 * -rf and -ri-trial) is a trial page rebuilt round one argument: the pains as a list, a one-tap blocker in place of
 * the scorer, the programme and an invitation panel in place of the transformation, the testimonials under the
 * mentor, and the options as cards in place of the comparison table. Each section reads which of these the page has.
 *
 * @param content         the page's copy: one of the files in content/
 * @param onIntakeSubmit  optional handler for the intake form. See IntakeModal.
 */
const JumpstartFocusPage = ({ content, onIntakeSubmit }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [playing, setPlaying] = useState(null)
  // scripts are running: below-the-fold content now waits for its reveal (see MwPage/components/Reveal)
  const [js, setJs] = useState(false)
  const jumpstartModalCopy = useJumpstartModal()
  const offer = useOffer()
  const stickyOn = useStickyCta()
  const { trial } = content
  // "Why not keep pitching?" sits before the FAQ on the raising investment page, before the mentor elsewhere
  const compareLate = !!content.compare.beforeFaq
  const modalCopy = trial ? { ...jumpstartModalCopy, ...trialModal } : jumpstartModalCopy
  // the seven weighted scores feed the scorer and the ladder, neither of which a trial page has
  const Scores = trial ? React.Fragment : ScoresProvider

  useEffect(() => setJs(true), [])

  const openModal = useCallback(() => setModalOpen(true), [])
  const closeModal = useCallback(() => setModalOpen(false), [])
  const closeVideo = useCallback(() => setPlaying(null), [])

  return (
    <FocusProvider value={content}>
      <Scores>
        <BuyProvider value={openModal}>
          <PageRoot data-js={js ? '' : undefined}>
            <HeroSection offer={offer} />
            <VideoSection offer={offer} />
            <PressStrip />
            {content.proof && <ProofSection />}
            {content.memberVideos && <MemberVideosSection onPlay={setPlaying} />}
            {content.problem && <ProblemSection />}
            {content.blocker && <BlockerSection offer={offer} />}
            {content.scorer && (trial ? <TrialScorerSection /> : <ScorerSection offer={offer} />)}
            {content.transformation &&
              (trial ? <TrialTransformationSection offer={offer} /> : <TransformationSection offer={offer} />)}
            {content.stage && <StageSection />}
            <JourneySection />
            {content.invite && <InviteSection offer={offer} />}
            {content.advice && <AdviceSection />}
            {!compareLate && <CompareSection offer={offer} />}
            <MentorSection />
            <TestimonialsSection onPlay={setPlaying} />
            <OfferSection offer={offer} />
            {compareLate && <CompareSection offer={offer} />}
            <FaqSection offer={offer} />
            <FinalSection offer={offer} />
            <Narrow>
              <FooterSection />
            </Narrow>
            <StickyCta
              show={stickyOn}
              price={offer.price}
              line={trial ? fillTrial(trial.sticky.line, offer) : undefined}
              cta={trial ? trial.sticky.cta : undefined}
            />

            <VideoLightbox video={playing} onClose={closeVideo} />
            <IntakeModal
              open={modalOpen}
              onClose={closeModal}
              onSubmit={onIntakeSubmit}
              collectAccountFields
              {...modalCopy}
            />
          </PageRoot>
        </BuyProvider>
      </Scores>
    </FocusProvider>
  )
}

export default JumpstartFocusPage
